import i18next from 'i18next';
import type { FC, PropsWithChildren } from 'react';
import { useEffect } from 'react';
import { I18nextProvider, initReactI18next } from 'react-i18next';

import { defaultTranslations } from '~/constants';
import { useLocalizationQuery } from '~/hooks';
import type { TranslationKey } from '~/models';

const DEFAULT_LANGUAGE_KEY = 'default';
const DEFAULT_NAMESPACE = 'translations';

const i18nextInstance = i18next
  .createInstance({
    keySeparator: '::',
    lng: DEFAULT_LANGUAGE_KEY,
    defaultNS: DEFAULT_NAMESPACE,
    resources: {
      [DEFAULT_LANGUAGE_KEY]: {
        [DEFAULT_NAMESPACE]: defaultTranslations,
      },
    },

    interpolation: {
      escapeValue: false, // react already safes from xss
      prefix: '{',
      suffix: '}',
    },

    react: {
      bindI18n: 'added removed languageChanged',
      bindI18nStore: 'added removed',
    },
  })
  .use(initReactI18next);

i18nextInstance.init();

export const I18nContainer: FC<
  PropsWithChildren<{
    preloadedTranslations?: Record<TranslationKey, string>;
  }>
> = ({ children, preloadedTranslations }) => {
  const { data } = useLocalizationQuery({
    isPaused: () => !!preloadedTranslations,
    suspense: !preloadedTranslations,
  });

  useEffect(() => {
    // If translations package has changed we will change provided package in i18next too
    i18nextInstance.addResourceBundle(
      DEFAULT_LANGUAGE_KEY,
      DEFAULT_NAMESPACE,
      data?.localizations ?? preloadedTranslations ?? {},
      false,
      true,
    );
  }, [data?.localizations, preloadedTranslations]);

  return <I18nextProvider i18n={i18nextInstance}>{children}</I18nextProvider>;
};
